import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@card-software/nexus-utilities';
import { UserGroup } from '../../models/authorization/enum/user-group.enum';
import { InventoryReportProxyService } from '../../proxies/inventory/inventory-report.proxy.service';
import { InventorySummaryProxyService } from '../../proxies/inventory/inventory-summary.proxy.service';
import { LocationProxyService } from '../../proxies/inventory/location.proxy.service';
import { ItemMasterProxyService } from '../../proxies/item/item-master.proxy.service';
import { OrderReportProxyService } from '../../proxies/order/order-report.proxy.service';
import { RollProxyService } from '../../proxies/production/roll.proxy';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { finalize } from 'rxjs';
import { MultiSelectFilterEvent } from 'primeng/multiselect';
import {
  ClosedMonthBalanceForm,
  ConsumableBalanceForm,
  ConsumableClosedMonthForm,
  DateRangeForm,
  HistoricalBalanceForm,
  ShipmentImportForm,
  WarehouseMovementForm,
} from './reporting.forms';

/** An option of the balance report dropdowns. An exit location (Shipped) shows the period's movement, not a balance. */
interface BalanceLocationOption {
  label: string;
  value: string;
  isExitLocation: boolean;
}

@Component({
  selector: 'app-reporting',
  templateUrl: './reporting.component.html',
  styleUrls: ['./reporting.component.scss'],
})
export class ReportingComponent implements OnInit {
  private readonly inventoryReportProxyService = inject(InventoryReportProxyService);
  private readonly inventorySummaryProxyService = inject(InventorySummaryProxyService);
  private readonly orderReportProxyService = inject(OrderReportProxyService);
  private readonly locationProxyService = inject(LocationProxyService);
  private readonly itemMasterProxyService = inject(ItemMasterProxyService);
  private readonly rollProxyService = inject(RollProxyService);

  isAdmin = AuthService.hasRole(UserGroup.InventoryReportingAdmin);
  canViewCost = AuthService.hasRole(UserGroup.FinanceCostingAdmin);
  isLoading = signal<boolean>(false);

  monthlyBillingForm = new FormGroup({
    startDate: new FormControl<Date | null>(null, [Validators.required]),
    endDate: new FormControl<Date | null>(null, [Validators.required]),
    includePrices: new FormControl<boolean>(false, { nonNullable: true }),
  });

  knittingBalanceForm = new FormGroup({
    includeCosts: new FormControl<boolean>(false, { nonNullable: true }),
  });

  knittingMovementForm = new FormGroup<DateRangeForm>({
    startDate: new FormControl<Date | null>(null, [Validators.required]),
    endDate: new FormControl<Date | null>(null, [Validators.required]),
    includeCosts: new FormControl<boolean>(false, { nonNullable: true }),
  });

  shipmentImportForm = new FormGroup<ShipmentImportForm>({
    startDate: new FormControl<Date | null>(null, [Validators.required]),
    endDate: new FormControl<Date | null>(null, [Validators.required]),
    includeCosts: new FormControl<boolean>(false, { nonNullable: true }),
    isConsignment: new FormControl<boolean>(false, { nonNullable: true }),
  });

  transferUnitForm = new FormGroup<DateRangeForm>({
    startDate: new FormControl<Date | null>(null, [Validators.required]),
    endDate: new FormControl<Date | null>(null, [Validators.required]),
    includeCosts: new FormControl<boolean>(false, { nonNullable: true }),
  });

  guatemalaGreigeForm = new FormGroup({
    startDate: new FormControl<Date | null>(null, [Validators.required]),
    endDate: new FormControl<Date | null>(null, [Validators.required]),
  });

  // PBI 4659 — Consumable Item Balance V2: date + warehouse selector feeding the historical
  // (as-of-date) 4-sheet export. V1 runs beside it and takes no inputs at all.
  consumableBalanceForm = new FormGroup<ConsumableBalanceForm>({
    asOfDate: new FormControl<Date | null>(null, [Validators.required]),
    locationCode: new FormControl<string | null>(null, [Validators.required]),
  });

  // PBI 4659 follow-up — closed-month consumable balance (INITIAL/IN/OUT/FINAL BALANCE for one
  // exact calendar month), mirrors closedMonthBalanceForm (Rolls).
  consumableClosedMonthForm = new FormGroup<ConsumableClosedMonthForm>({
    year: new FormControl<number | null>(null, [Validators.required]),
    month: new FormControl<number | null>(null, [Validators.required]),
    locationCode: new FormControl<string | null>(null, [Validators.required]),
  });

  consumableInboundForm = new FormGroup({
    startDate: new FormControl<Date | null>(null, [Validators.required]),
    endDate: new FormControl<Date | null>(null, [Validators.required]),
    locationCode: new FormControl<string | null>(null, [Validators.required]),
  });

  consumableOutboundForm = new FormGroup({
    startDate: new FormControl<Date | null>(null, [Validators.required]),
    endDate: new FormControl<Date | null>(null, [Validators.required]),
    locationCode: new FormControl<string | null>(null, [Validators.required]),
  });

  warehouseMovementForm = new FormGroup<WarehouseMovementForm>({
    startDate: new FormControl<Date | null>(null, [Validators.required]),
    endDate: new FormControl<Date | null>(null, [Validators.required]),
    locationCode: new FormControl<string | null>(null, [Validators.required]),
  });

  historicalBalanceForm = new FormGroup<HistoricalBalanceForm>({
    asOfDate: new FormControl<Date | null>(null, [Validators.required]),
    locationCode: new FormControl<string | null>(null, [Validators.required]),
    includeCost: new FormControl<boolean>(false, { nonNullable: true }),
  });

  closedMonthBalanceForm = new FormGroup<ClosedMonthBalanceForm>({
    year: new FormControl<number | null>(null, [Validators.required]),
    month: new FormControl<number | null>(null, [Validators.required]),
    locationCode: new FormControl<string | null>(null, [Validators.required]),
    includeCost: new FormControl<boolean>(false, { nonNullable: true }),
  });

  balanceAuditForm = new FormGroup({
    locationCode: new FormControl<string | null>(null, [Validators.required]),
  });

  pendingRollsForm = new FormGroup({
    locationCodes: new FormControl<string[]>([], { nonNullable: true, validators: [Validators.required] }),
  });

  warehouseLocations = signal<{ label: string; value: string }[]>([]);
  /** The selected site's balance locations (GET location/balance-locations), in their dropdown order. */
  balanceLocations = signal<BalanceLocationOption[]>([]);
  knittingLocations = signal<{ label: string; value: string }[]>([]);
  consumableWarehouses = signal<{ label: string; value: string }[]>([]);
  balanceAuditItems = signal<{ label: string; value: string }[]>([]);
  balanceAuditSelectedItems: string[] = [];

  readonly monthOptions = [
    { label: 'January', value: 1 },
    { label: 'February', value: 2 },
    { label: 'March', value: 3 },
    { label: 'April', value: 4 },
    { label: 'May', value: 5 },
    { label: 'June', value: 6 },
    { label: 'July', value: 7 },
    { label: 'August', value: 8 },
    { label: 'September', value: 9 },
    { label: 'October', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 },
  ];

  readonly yearOptions = Array.from({ length: new Date().getFullYear() - 2020 + 1 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { label: year.toString(), value: year };
  });

  loadingMonthlyBilling = signal<boolean>(false);
  loadingKnittingBalance = signal<boolean>(false);
  loadingKnittingMovement = signal<boolean>(false);
  loadingShipmentImport = signal<boolean>(false);
  loadingTransferUnit = signal<boolean>(false);
  loadingGuatemalaGreige = signal<boolean>(false);
  loadingConsumableInbound = signal<boolean>(false);
  loadingConsumableOutbound = signal<boolean>(false);
  loadingConsumableBalance = signal<boolean>(false);
  loadingConsumableBalanceV2 = signal<boolean>(false);
  loadingConsumableClosedMonth = signal<boolean>(false);
  loadingWarehouseMovement = signal<boolean>(false);
  loadingHistoricalBalance = signal<boolean>(false);
  loadingClosedMonthBalance = signal<boolean>(false);
  loadingBalanceAudit = signal<boolean>(false);
  loadingPendingRolls = signal<boolean>(false);

  isAnyReportLoading = computed(
    () =>
      this.loadingMonthlyBilling() ||
      this.loadingKnittingBalance() ||
      this.loadingKnittingMovement() ||
      this.loadingShipmentImport() ||
      this.loadingTransferUnit() ||
      this.loadingGuatemalaGreige() ||
      this.loadingConsumableInbound() ||
      this.loadingConsumableOutbound() ||
      this.loadingConsumableBalance() ||
      this.loadingConsumableBalanceV2() ||
      this.loadingConsumableClosedMonth() ||
      this.loadingWarehouseMovement() ||
      this.loadingHistoricalBalance() ||
      this.loadingClosedMonthBalance() ||
      this.loadingBalanceAudit() ||
      this.loadingPendingRolls(),
  );

  readonly MAX_DATE_RANGE_DAYS = 60;

  monthlyBillingMaxDate = computed<Date | undefined>(() => {
    const startDate = this.monthlyBillingForm.value.startDate;
    if (!startDate) return undefined;
    const maxDate = new Date(startDate);
    maxDate.setDate(maxDate.getDate() + this.MAX_DATE_RANGE_DAYS);
    return maxDate;
  });

  knittingMovementMaxDate = computed<Date | undefined>(() => {
    const startDate = this.knittingMovementForm.value.startDate;
    if (!startDate) return undefined;
    const maxDate = new Date(startDate);
    maxDate.setDate(maxDate.getDate() + this.MAX_DATE_RANGE_DAYS);
    return maxDate;
  });

  shipmentImportMaxDate = computed<Date | undefined>(() => {
    const startDate = this.shipmentImportForm.value.startDate;
    if (!startDate) return undefined;
    const maxDate = new Date(startDate);
    maxDate.setDate(maxDate.getDate() + this.MAX_DATE_RANGE_DAYS);
    return maxDate;
  });

  transferUnitMaxDate = computed<Date | undefined>(() => {
    const startDate = this.transferUnitForm.value.startDate;
    if (!startDate) return undefined;
    const maxDate = new Date(startDate);
    maxDate.setDate(maxDate.getDate() + this.MAX_DATE_RANGE_DAYS);
    return maxDate;
  });

  guatemalaGreigeMaxDate = computed<Date | undefined>(() => {
    const startDate = this.guatemalaGreigeForm.value.startDate;
    if (!startDate) return undefined;
    const maxDate = new Date(startDate);
    maxDate.setDate(maxDate.getDate() + this.MAX_DATE_RANGE_DAYS);
    return maxDate;
  });

  consumableInboundMaxDate = computed<Date | undefined>(() => {
    const startDate = this.consumableInboundForm.value.startDate;
    if (!startDate) return undefined;
    const maxDate = new Date(startDate);
    maxDate.setDate(maxDate.getDate() + this.MAX_DATE_RANGE_DAYS);
    return maxDate;
  });

  consumableOutboundMaxDate = computed<Date | undefined>(() => {
    const startDate = this.consumableOutboundForm.value.startDate;
    if (!startDate) return undefined;
    const maxDate = new Date(startDate);
    maxDate.setDate(maxDate.getDate() + this.MAX_DATE_RANGE_DAYS);
    return maxDate;
  });

  warehouseMovementMaxDate = computed<Date | undefined>(() => {
    const startDate = this.warehouseMovementForm.value.startDate;
    if (!startDate) return undefined;
    const maxDate = new Date(startDate);
    maxDate.setDate(maxDate.getDate() + this.MAX_DATE_RANGE_DAYS);
    return maxDate;
  });

  private readonly WAREHOUSE_LOCATION_CODES = [
    'NT.WH-GR-1',
    'NT.WH-BRD-1',
    'NT.WA-BATCHING-1',
    'NT.WH-FG-1',
    'NT.WH-RG-1',
    'NT.WH-FIN-1',
    'NT.WH-DY-1',
  ];

  private readonly KNITTING_LOCATION_CODES = ['NT.WA-KNITTING-1', 'SIMTEX.WA-KNITTING-2'];

  ngOnInit(): void {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    this.monthlyBillingForm.patchValue({ startDate: yesterday, endDate: today });
    this.knittingMovementForm.patchValue({ startDate: yesterday, endDate: today });
    this.shipmentImportForm.patchValue({ startDate: yesterday, endDate: today });
    this.transferUnitForm.patchValue({ startDate: yesterday, endDate: today });
    this.guatemalaGreigeForm.patchValue({ startDate: yesterday, endDate: today });
    this.consumableInboundForm.patchValue({ startDate: yesterday, endDate: today });
    this.consumableOutboundForm.patchValue({ startDate: yesterday, endDate: today });
    this.warehouseMovementForm.patchValue({ startDate: yesterday, endDate: today });
    this.historicalBalanceForm.patchValue({ asOfDate: today });
    this.consumableBalanceForm.patchValue({ asOfDate: today });

    this.loadWarehouseLocations();
    this.loadBalanceLocations();
    this.loadConsumableWarehouses();
    this.loadBalanceAuditItems('');
    this.loadKnittingLocations();
  }

  private loadKnittingLocations(): void {
    this.locationProxyService.batchGetByCodes(this.KNITTING_LOCATION_CODES).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.knittingLocations.set(response.data.map((loc) => ({ label: loc.name?.en ?? loc.locationCode, value: loc.locationCode })));
          // Pre-select all knitting work areas by default.
          this.pendingRollsForm.patchValue({ locationCodes: response.data.map((loc) => loc.locationCode) });
        }
      },
    });
  }

  private loadWarehouseLocations(): void {
    this.locationProxyService.batchGetByCodes(this.WAREHOUSE_LOCATION_CODES).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.warehouseLocations.set(response.data.map((loc) => ({ label: loc.name?.en ?? loc.locationCode, value: loc.locationCode })));
        }
      },
    });
  }

  // The API returns the selected site's balance locations already ordered (BalanceSortOrder).
  private loadBalanceLocations(): void {
    this.locationProxyService.getBalanceLocations().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.balanceLocations.set(
            response.data.map((loc) => ({
              label: loc.name?.en ?? loc.locationCode,
              value: loc.locationCode,
              isExitLocation: loc.isExitLocation ?? false,
            })),
          );
        }
      },
    });
  }

  private loadConsumableWarehouses(): void {
    this.locationProxyService.getConsumableWarehouses().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.consumableWarehouses.set(response.data.map((loc) => ({ label: loc.name?.en ?? loc.locationCode, value: loc.locationCode })));
        }
      },
    });
  }

  private loadBalanceAuditItems(query: string): void {
    this.itemMasterProxyService.search({ name: query, itemCode: query }, true, true).subscribe({
      next: (response) => {
        if (response.success && response.data?.items) {
          this.balanceAuditItems.set(
            response.data.items.map((item) => ({
              label: `(${item.itemCode}) ${item.itemDisplayName?.en ?? ''}`,
              value: item.itemCode,
            })),
          );
        }
      },
    });
  }

  private validateDateRange(startDate: Date | null, endDate: Date | null): boolean {
    if (!startDate || !endDate) {
      GlobalMessageUtil.warn('Validation Error', 'Please select both start and end dates');
      return false;
    }

    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > this.MAX_DATE_RANGE_DAYS) {
      GlobalMessageUtil.warn('Validation Error', `Date range cannot exceed ${this.MAX_DATE_RANGE_DAYS} days`);
      return false;
    }

    if (startDate > endDate) {
      GlobalMessageUtil.warn('Validation Error', 'Start date must be before end date');
      return false;
    }

    return true;
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  onBalanceAuditItemSearch(event: MultiSelectFilterEvent): void {
    this.loadBalanceAuditItems(event.filter ?? '');
  }

  generateMonthlyBillingReport(): void {
    if (!this.monthlyBillingForm.valid) {
      GlobalMessageUtil.warn('Validation Error', 'Please fill in all required fields');
      return;
    }

    const startDate = this.monthlyBillingForm.value.startDate;
    const endDate = this.monthlyBillingForm.value.endDate;

    if (!this.validateDateRange(startDate!, endDate!)) return;

    const includePrices = this.isAdmin ? this.monthlyBillingForm.value.includePrices : false;

    this.loadingMonthlyBilling.set(true);

    this.inventoryReportProxyService
      .monthlyBillingReport(startDate!, endDate!, includePrices!)
      .pipe(finalize(() => this.loadingMonthlyBilling.set(false)))
      .subscribe({
        next: (blob) => {
          this.downloadBlob(
            blob,
            `MonthlyBilling_${startDate!.toISOString().split('T')[0]}_to_${endDate!.toISOString().split('T')[0]}.xlsx`,
          );
          GlobalMessageUtil.success('Success', 'Monthly Billing report generated successfully');
        },
      });
  }

  generateKnittingBalanceReport(): void {
    this.loadingKnittingBalance.set(true);

    const includeCosts = this.isAdmin ? this.knittingBalanceForm.value.includeCosts : false;

    this.inventoryReportProxyService
      .knittingInventoryBalanceReport(true, includeCosts!)
      .pipe(finalize(() => this.loadingKnittingBalance.set(false)))
      .subscribe({
        next: (blob) => {
          this.downloadBlob(blob, `Knitting_Balance_${new Date().toISOString().split('T')[0]}.xlsx`);
          GlobalMessageUtil.success('Success', 'Knitting Current Balance report generated successfully');
        },
      });
  }

  generateKnittingMovementReport(): void {
    if (!this.knittingMovementForm.valid) {
      GlobalMessageUtil.warn('Validation Error', 'Please fill in all required fields');
      return;
    }

    const startDate = this.knittingMovementForm.value.startDate;
    const endDate = this.knittingMovementForm.value.endDate;
    const includeCosts = this.isAdmin ? this.knittingMovementForm.value.includeCosts : false;

    if (!this.validateDateRange(startDate!, endDate!)) return;

    this.loadingKnittingMovement.set(true);

    this.inventoryReportProxyService
      .requisitionReport({ startDate: startDate!, endDate: endDate!, includeCosts })
      .pipe(finalize(() => this.loadingKnittingMovement.set(false)))
      .subscribe({
        next: (blob) => {
          this.downloadBlob(
            blob,
            `Knitting_Movement_${startDate!.toISOString().split('T')[0]}_to_${endDate!.toISOString().split('T')[0]}.xlsx`,
          );
          GlobalMessageUtil.success('Success', 'Knitting Work Areas Movement report generated successfully');
        },
      });
  }

  generateShipmentImportReport(): void {
    if (!this.shipmentImportForm.valid) {
      GlobalMessageUtil.warn('Validation Error', 'Please fill in all required fields');
      return;
    }

    const startDate = this.shipmentImportForm.value.startDate;
    const endDate = this.shipmentImportForm.value.endDate;
    const isConsignment = this.shipmentImportForm.value.isConsignment;
    const includeCosts = this.isAdmin ? this.shipmentImportForm.value.includeCosts : false;

    if (!this.validateDateRange(startDate!, endDate!)) return;

    this.loadingShipmentImport.set(true);

    this.orderReportProxyService
      .yarnImportReport({ fromDate: startDate!, toDate: endDate! }, includeCosts!, !isConsignment!)
      .pipe(finalize(() => this.loadingShipmentImport.set(false)))
      .subscribe({
        next: (blob) => {
          const shipmentType = isConsignment ? 'Consignment' : 'Standard';
          this.downloadBlob(
            blob,
            `Shipment_Import_${shipmentType}_${startDate!.toISOString().split('T')[0]}_to_${endDate!.toISOString().split('T')[0]}.xlsx`,
          );
          GlobalMessageUtil.success('Success', `${shipmentType} Shipment Import report generated successfully`);
        },
      });
  }

  generateTransferUnitReport(): void {
    if (!this.transferUnitForm.valid) {
      GlobalMessageUtil.warn('Validation Error', 'Please fill in all required fields');
      return;
    }

    const startDate = this.transferUnitForm.value.startDate;
    const endDate = this.transferUnitForm.value.endDate;
    const includeCosts = this.isAdmin ? this.transferUnitForm.value.includeCosts : false;

    if (!this.validateDateRange(startDate!, endDate!)) return;

    this.loadingTransferUnit.set(true);

    this.inventoryReportProxyService
      .transferUnitReport({ startDate: startDate!, endDate: endDate!, includeCosts })
      .pipe(finalize(() => this.loadingTransferUnit.set(false)))
      .subscribe({
        next: (blob) => {
          this.downloadBlob(
            blob,
            `Transfer_Unit_${startDate!.toISOString().split('T')[0]}_to_${endDate!.toISOString().split('T')[0]}.xlsx`,
          );
          GlobalMessageUtil.success('Success', 'Transfer Unit report generated successfully');
        },
      });
  }

  generateGuatemalaGreigeReport(): void {
    if (!this.guatemalaGreigeForm.valid) {
      GlobalMessageUtil.warn('Validation Error', 'Please fill in all required fields');
      return;
    }

    const startDate = this.guatemalaGreigeForm.value.startDate;
    const endDate = this.guatemalaGreigeForm.value.endDate;

    if (!this.validateDateRange(startDate!, endDate!)) return;

    this.loadingGuatemalaGreige.set(true);

    this.inventoryReportProxyService
      .guatemalaGreigeMovementReport({ startDate: startDate!, endDate: endDate! })
      .pipe(finalize(() => this.loadingGuatemalaGreige.set(false)))
      .subscribe({
        next: (blob) => {
          this.downloadBlob(
            blob,
            `Guatemala_Greige_${startDate!.toISOString().split('T')[0]}_to_${endDate!.toISOString().split('T')[0]}.xlsx`,
          );
          GlobalMessageUtil.success('Success', 'Incoming Guatemala Greige report generated successfully');
        },
      });
  }

  generateConsumableInboundReport(): void {
    if (!this.consumableInboundForm.valid) {
      GlobalMessageUtil.warn('Validation Error', 'Please fill in all required fields');
      return;
    }

    const startDate = this.consumableInboundForm.value.startDate;
    const endDate = this.consumableInboundForm.value.endDate;
    const locationCode = this.consumableInboundForm.value.locationCode!;

    if (!this.validateDateRange(startDate!, endDate!)) return;

    this.loadingConsumableInbound.set(true);

    this.inventoryReportProxyService
      .consumableInboundReport({ startDate: startDate!, endDate: endDate!, locationCode })
      .pipe(finalize(() => this.loadingConsumableInbound.set(false)))
      .subscribe({
        next: (blob) => {
          this.downloadBlob(
            blob,
            `Consumable_Inbound_${locationCode}_${startDate!.toISOString().split('T')[0]}_to_${endDate!.toISOString().split('T')[0]}.xlsx`,
          );
          GlobalMessageUtil.success('Success', 'Consumable Inbound report generated successfully');
        },
      });
  }

  generateConsumableOutboundReport(): void {
    if (!this.consumableOutboundForm.valid) {
      GlobalMessageUtil.warn('Validation Error', 'Please fill in all required fields');
      return;
    }

    const startDate = this.consumableOutboundForm.value.startDate;
    const endDate = this.consumableOutboundForm.value.endDate;
    const locationCode = this.consumableOutboundForm.value.locationCode!;

    if (!this.validateDateRange(startDate!, endDate!)) return;

    this.loadingConsumableOutbound.set(true);

    this.inventoryReportProxyService
      .consumableOutboundReport({ startDate: startDate!, endDate: endDate!, locationCode })
      .pipe(finalize(() => this.loadingConsumableOutbound.set(false)))
      .subscribe({
        next: (blob) => {
          this.downloadBlob(
            blob,
            `Consumable_Outbound_${locationCode}_${startDate!.toISOString().split('T')[0]}_to_${endDate!.toISOString().split('T')[0]}.xlsx`,
          );
          GlobalMessageUtil.success('Success', 'Consumable Outbound report generated successfully');
        },
      });
  }

  /**
   * V1 — the live balance the business has been receiving all along: no date, no warehouse, one
   * sheet of current stock. Kept alongside V2 so the two can be compared side by side.
   */
  generateConsumableBalanceReport(): void {
    this.loadingConsumableBalance.set(true);

    this.inventoryReportProxyService
      .consumableBalanceReport()
      .pipe(finalize(() => this.loadingConsumableBalance.set(false)))
      .subscribe({
        next: (blob) => {
          this.downloadBlob(blob, `Consumable_Balance_${new Date().toISOString().split('T')[0]}.xlsx`);
          GlobalMessageUtil.success('Success', 'Consumable Item Balance report generated successfully');
        },
      });
  }

  /**
   * V2 (PBI 4659) — balance as of a date for one warehouse, as a 4-sheet book
   * (INITIAL / IN / OUT / FINAL BALANCE) built from the monthly snapshots.
   */
  generateConsumableBalanceV2Report(): void {
    if (!this.consumableBalanceForm.valid) {
      GlobalMessageUtil.warn('Validation Error', 'Please fill in all required fields');
      return;
    }

    const asOfDate = this.consumableBalanceForm.value.asOfDate;
    const locationCode = this.consumableBalanceForm.value.locationCode;

    this.loadingConsumableBalanceV2.set(true);

    this.inventoryReportProxyService
      .consumableHistoricalBalanceExport(locationCode!, asOfDate!)
      .pipe(finalize(() => this.loadingConsumableBalanceV2.set(false)))
      .subscribe({
        next: (blob) => {
          this.downloadBlob(blob, `Consumable_Balance_V2_${locationCode}_${asOfDate!.toISOString().split('T')[0]}.xlsx`);
          GlobalMessageUtil.success('Success', 'Consumable Item Balance V2 report generated successfully');
        },
      });
  }

  generateConsumableClosedMonthReport(): void {
    if (!this.consumableClosedMonthForm.valid) {
      GlobalMessageUtil.warn('Validation Error', 'Please fill in all required fields');
      return;
    }

    const year = this.consumableClosedMonthForm.value.year;
    const month = this.consumableClosedMonthForm.value.month;
    const locationCode = this.consumableClosedMonthForm.value.locationCode;

    this.loadingConsumableClosedMonth.set(true);

    this.inventoryReportProxyService
      .consumableClosedMonthBalanceExport(locationCode!, year!, month!)
      .pipe(finalize(() => this.loadingConsumableClosedMonth.set(false)))
      .subscribe({
        next: (blob) => {
          const monthStr = month!.toString().padStart(2, '0');
          this.downloadBlob(blob, `Consumable_Balance_ClosedMonth_${locationCode}_${year}-${monthStr}.xlsx`);
          GlobalMessageUtil.success('Success', 'Consumable Closed Month Balance report generated successfully');
        },
      });
  }

  generateWarehouseMovementReport(): void {
    if (!this.warehouseMovementForm.valid) {
      GlobalMessageUtil.warn('Validation Error', 'Please fill in all required fields');
      return;
    }

    const startDate = this.warehouseMovementForm.value.startDate;
    const endDate = this.warehouseMovementForm.value.endDate;
    const locationCode = this.warehouseMovementForm.value.locationCode;

    if (!this.validateDateRange(startDate!, endDate!)) return;

    this.loadingWarehouseMovement.set(true);

    this.inventoryReportProxyService
      .warehouseMovementReport(locationCode!, startDate!, endDate!)
      .pipe(finalize(() => this.loadingWarehouseMovement.set(false)))
      .subscribe({
        next: (blob) => {
          this.downloadBlob(
            blob,
            `Warehouse_Movement_${locationCode}_${startDate!.toISOString().split('T')[0]}_to_${endDate!.toISOString().split('T')[0]}.xlsx`,
          );
          GlobalMessageUtil.success('Success', 'Warehouse Movement report generated successfully');
        },
      });
  }

  generateHistoricalBalanceReport(): void {
    if (!this.historicalBalanceForm.valid) {
      GlobalMessageUtil.warn('Validation Error', 'Please fill in all required fields');
      return;
    }

    const asOfDate = this.historicalBalanceForm.value.asOfDate;
    const locationCode = this.historicalBalanceForm.value.locationCode;
    const includeCost = this.canViewCost ? this.historicalBalanceForm.value.includeCost! : false;

    this.loadingHistoricalBalance.set(true);

    this.inventoryReportProxyService
      .historicalBalanceExport(locationCode!, asOfDate!, includeCost)
      .pipe(finalize(() => this.loadingHistoricalBalance.set(false)))
      .subscribe({
        next: (blob) => {
          this.downloadBlob(blob, `InventoryBalance_Detailed_${locationCode}_${asOfDate!.toISOString().split('T')[0]}.xlsx`);
          GlobalMessageUtil.success('Success', 'Historical Inventory Balance report generated successfully');
        },
      });
  }

  generateConsumableBalanceAuditReport(): void {
    if (!this.balanceAuditForm.valid || this.balanceAuditSelectedItems.length === 0) {
      GlobalMessageUtil.warn('Validation Error', 'Please select a warehouse and at least one item');
      return;
    }

    const locationCode = this.balanceAuditForm.value.locationCode!;
    const items = this.balanceAuditSelectedItems.map((itemCode) => ({ itemCode, initialBalance: 0, adjustment: 0 }));

    this.loadingBalanceAudit.set(true);

    this.inventorySummaryProxyService
      .balanceAuditExport(locationCode, items)
      .pipe(finalize(() => this.loadingBalanceAudit.set(false)))
      .subscribe({
        next: (blob) => {
          this.downloadBlob(blob, `Consumable_Balance_Audit_${locationCode}_${new Date().toISOString().split('T')[0]}.xlsx`);
          GlobalMessageUtil.success('Success', 'Consumable Balance Audit report generated successfully');
        },
      });
  }

  generateClosedMonthBalanceReport(): void {
    if (!this.closedMonthBalanceForm.valid) {
      GlobalMessageUtil.warn('Validation Error', 'Please fill in all required fields');
      return;
    }

    const year = this.closedMonthBalanceForm.value.year;
    const month = this.closedMonthBalanceForm.value.month;
    const locationCode = this.closedMonthBalanceForm.value.locationCode;
    const includeCost = this.canViewCost ? this.closedMonthBalanceForm.value.includeCost! : false;

    this.loadingClosedMonthBalance.set(true);

    this.inventoryReportProxyService
      .closedMonthBalanceExport(locationCode!, year!, month!, includeCost)
      .pipe(finalize(() => this.loadingClosedMonthBalance.set(false)))
      .subscribe({
        next: (blob) => {
          const monthStr = month!.toString().padStart(2, '0');
          this.downloadBlob(blob, `InventoryBalance_ClosedMonth_${locationCode}_${year}-${monthStr}.xlsx`);
          GlobalMessageUtil.success('Success', 'Closed Month Balance report generated successfully');
        },
      });
  }

  generatePendingRollsReport(): void {
    const locationCodes = this.pendingRollsForm.value.locationCodes ?? [];
    if (locationCodes.length === 0) {
      GlobalMessageUtil.warn('Validation Error', 'Please select at least one location');
      return;
    }

    this.loadingPendingRolls.set(true);

    this.rollProxyService
      .exportPendingRolls({ locationCodes, numbers: [] })
      .pipe(finalize(() => this.loadingPendingRolls.set(false)))
      .subscribe({
        next: (blob) => {
          this.downloadBlob(blob, `Rollos_No_Transferidos_${new Date().toISOString().split('T')[0]}.xlsx`);
          GlobalMessageUtil.success('Success', 'Scanned rolls not transferred report generated successfully');
        },
      });
  }
}
